import { GetServerSideProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { client } from '~/api/client'
import {
  getMemberByUserName,
  getWebMembersUserNameWaysV1,
  Schemas,
} from '~/api/gen'
import { Layout } from '~/components/Layout'
import { Meta } from '~/components/Meta'
import { MemberVideoList } from '~/features/MemberVideoList/MemberVideoList'
import { getMetadata } from '~/features/MyPageVideoList/lib/getMetadata'
import { buildMemberDlink } from '~/features/shared/memberDetail/lib/buildMemberDlink'
import { getPageNumberFromQueryValue } from '~/lib/getPageNumberFromQueryValue'
import { sendGtmEvOnClickDownloadHeader } from '~/lib/sendGtmEvOnClickDownloadHeader'

const FETCH_PAGE_SIZE = 60

const GoogleAdScripts = dynamic(
  () => import('~/features/shared/ad/components/GoogleAdScripts'),
  { ssr: false }
)
type Props = {
  member: Schemas.member_large_detail
  videoList: Schemas.get_web_members_user_name_ways_v1_response
  pageNumber: number
}

const VideoListPage: NextPage<Props> = ({ member, videoList, pageNumber }) => {
  const isFirstPage = pageNumber === 1
  return (
    <Layout
      breadcrumbList={[
        member.shop
          ? {
              name: member.shop.name,
              path: `/shop/${member.shop.id}/`,
            }
          : [],
        {
          name: member.nick_name,
          path: `/${member.user_name}/`,
        },
        {
          name: 'ノウハウ動画一覧',
        },
      ].flat()}
      headerProps={{
        headerType: 'border',
        appLink: `wear2020://wear.jp/user/${member.id}/`,
        hideAppIcon: isFirstPage,
      }}
      appUrl={`wear2020://wear.jp/user/${member.id}/`}
      appDownloadUrl={buildMemberDlink(member.id)}
      abstractUrl="wear.jp/username/videos/"
      onClickAppLink={sendGtmEvOnClickDownloadHeader}
    >
      <GoogleAdScripts pages={[{ label: 'pc:user' }, { label: 'sp:user' }]} />
      <Meta {...getMetadata(member, videoList.total_count)} />
      <MemberVideoList member={member} videoCollection={videoList} />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
}) => {
  const userName = query.username as string
  const { pageNumber, isValid } = getPageNumberFromQueryValue(query.pageno)
  if (!isValid) {
    return {
      redirect: {
        statusCode: 301,
        destination: `/${userName}/videos/`,
      },
    }
  }
  const [memberResponse, videoListResponse] = await Promise.all([
    getMemberByUserName(client, {
      parameter: { user_name: userName },
    }),
    getWebMembersUserNameWaysV1(client, {
      parameter: {
        user_name: userName,
        pagesize: FETCH_PAGE_SIZE,
        pageno: pageNumber,
      },
    }),
  ])

  if (memberResponse.type === 'error') {
    console.error(memberResponse.error)
    if (memberResponse.raw.status === 404) return { notFound: true }
    throw new Error('Failed to get member')
  }
  if (videoListResponse.type === 'error') {
    console.error(videoListResponse.error)
    if (videoListResponse.raw.status === 404) return { notFound: true }
    throw new Error('Failed to get video')
  }

  if (pageNumber > 1 && videoListResponse.data.ways.length === 0) {
    return {
      redirect: {
        statusCode: 301,
        destination: `/${userName}/videos/`,
      },
    }
  }
  return {
    props: {
      member: memberResponse.data,
      videoList: videoListResponse.data,
      pageNumber,
    },
  }
}

export default VideoListPage
